import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  RecentScreen,
  addToRecentScreens,
  sanitizeStoredScreens,
} from '../helpers/recent-screens.helper';
import { JwtService } from './jwt.service';
import { PREMISSIONS } from '../enums/permissions';

/**
 * Default screens shown on first use (no history yet).
 * Only non-sensitive route paths — no financial data.
 * Ordered by most common ERP daily usage.
 */
/**
 * Default quick-access items — shown when the user has no personal history yet.
 *
 * Priority order (highest → lowest):
 *  1. Daily transaction screens  — Sales Invoices, Purchases Invoices
 *  2. Accounting vouchers        — Receipt, Payment, Journal
 *  3. POS                        — POS Invoices
 *  4. Inventory                  — Products, Stock Movement, Stores
 *  5. Sales workflow             — Sales Orders, Sales Return, Customers
 *  6. Purchases workflow         — Purchase Orders, Vendors
 *  7. Reports                    — Account Statement, Trial Balance
 *
 * 15 items ensures the panel is always full on xxl screens.
 * On smaller screens the template slices fewer items via `visibleScreenCount`.
 *
 * Icons use the ki-duotone icon set already used across the app.
 */
const DEFAULT_RECENT_SCREENS: RecentScreen[] = [
  // ── Tier 1: Daily Transactions ──────────────────────────────
  {
    link: '/sales/sales-invoices/create-invoice/create/new',
    titleAr: 'إنشاء فاتورة مبيعات',
    titleEn: 'New Sales Invoice',
    icon: 'ki-bill',
    visitedAt: 0,
    createPermissionKey: PREMISSIONS.Sales.Create,
    viewPermissionKey: PREMISSIONS.Sales.View,
  },
  {
    link: '/purchases/purchases-invoices/create-invoice/create/new',
    titleAr: 'إنشاء فاتورة مشتريات',
    titleEn: 'New Purchase Invoice',
    icon: 'ki-purchase',
    visitedAt: 0,
    createPermissionKey: PREMISSIONS.Purches.Create,
    viewPermissionKey: PREMISSIONS.Purches.View,
  },
  // ── Tier 2: Accounting Vouchers ─────────────────────────────
  {
    link: '/accounting/receipt-vouchers/create-or-update-receipt-voucher/create/new',
    titleAr: 'إنشاء سند قبض',
    titleEn: 'New Receipt Voucher',
    icon: 'ki-dollar',
    visitedAt: 0,
    createPermissionKey: PREMISSIONS.ReceiptVoucher.Create,
    viewPermissionKey: PREMISSIONS.ReceiptVoucher.View,
  },
  {
    link: '/accounting/payment-vouchers/create-or-update-payment-voucher/create/new',
    titleAr: 'إنشاء سند صرف',
    titleEn: 'New Payment Voucher',
    icon: 'ki-wallet',
    visitedAt: 0,
    createPermissionKey: PREMISSIONS.PaymentVoucher.Create,
    viewPermissionKey: PREMISSIONS.PaymentVoucher.View,
  },
  {
    link: '/accounting/journals/create-or-update-journal/create/new',
    titleAr: 'إنشاء قيد يومية',
    titleEn: 'New Journal Entry',
    icon: 'ki-book-open',
    visitedAt: 0,
    createPermissionKey: PREMISSIONS.Journal.Create,
    viewPermissionKey: PREMISSIONS.Journal.View,
  },
  // ── Tier 3: POS ─────────────────────────────────────────────
  {
    link: '/pos-invoices',
    titleAr: 'نقاط البيع',
    titleEn: 'POS Invoices',
    icon: 'ki-shop',
    visitedAt: 0,
    viewPermissionKey: PREMISSIONS.POSTerminals.View,
    createPermissionKey: PREMISSIONS.POSTerminals.Create,
  },
  // ── Tier 4: Inventory ───────────────────────────────────────
  {
    link: '/stock/products/create-or-update-product/create/new',
    titleAr: 'إضافة منتج',
    titleEn: 'New Product',
    icon: 'ki-cube-3',
    visitedAt: 0,
    createPermissionKey: PREMISSIONS.Products.Create,
    viewPermissionKey: PREMISSIONS.Products.View,
  },
  {
    link: '/stock/stores',
    titleAr: 'المستودعات',
    titleEn: 'Stores / Warehouses',
    icon: 'ki-abstract-26',
    visitedAt: 0,
    viewPermissionKey: PREMISSIONS.Store.View,
    createPermissionKey: PREMISSIONS.Store.Create,
  },
  // ── Tier 5: Sales Workflow ───────────────────────────────────
  {
    link: '/sales/sales-orders/create-sales-order/create/new',
    titleAr: 'إنشاء طلب بيع',
    titleEn: 'New Sales Order',
    icon: 'ki-delivery',
    visitedAt: 0,
    createPermissionKey: PREMISSIONS.SalesOrders.Create,
    viewPermissionKey: PREMISSIONS.SalesOrders.View,
  },
  {
    link: '/sales/customers/create-customer/create/new',
    titleAr: 'إضافة عميل',
    titleEn: 'New Customer',
    icon: 'ki-people',
    visitedAt: 0,
    createPermissionKey: PREMISSIONS.Customer.Create,
    viewPermissionKey: PREMISSIONS.Customer.View,
  },
  // ── Tier 6: Purchases Workflow ──────────────────────────────
  {
    link: '/purchases/purchase-orders/create-purchase-order/create/new',
    titleAr: 'إنشاء أمر شراء',
    titleEn: 'New Purchase Order',
    icon: 'ki-abstract-28',
    visitedAt: 0,
    createPermissionKey: PREMISSIONS.PurchaseOrders.Create,
    viewPermissionKey: PREMISSIONS.PurchaseOrders.View,
  },
  {
    link: '/purchases/vendors/create-vendor/create/new',
    titleAr: 'إضافة مورد',
    titleEn: 'New Vendor',
    icon: 'ki-truck',
    visitedAt: 0,
    createPermissionKey: PREMISSIONS.Vendor.Create,
    viewPermissionKey: PREMISSIONS.Vendor.View,
  },
  // ── Tier 7: Reports (View only — no create screen) ──────────
  {
    link: '/report/simple-account-statement',
    titleAr: 'كشف الحساب',
    titleEn: 'Account Statement',
    icon: 'ki-graph-3',
    visitedAt: 0,
    viewPermissionKey: PREMISSIONS.AccountStatement.View,
  },
  {
    link: '/report/trail-balance',
    titleAr: 'ميزان المراجعة',
    titleEn: 'Trial Balance',
    icon: 'ki-chart-pie-4',
    visitedAt: 0,
    viewPermissionKey: PREMISSIONS.TrialBalance.View,
  },
  {
    link: '/report/actual-inventory',
    titleAr: 'الجرد الفعلي',
    titleEn: 'Actual Inventory',
    icon: 'ki-chart-line-up',
    visitedAt: 0,
    viewPermissionKey: PREMISSIONS.ActualInventory.View,
  },
];

/**
 * RecentScreensService
 * ─────────────────────────────────────────────
 * Single source of truth for "recently visited / searched screens".
 *
 * Persistence: localStorage (user-scoped key).
 * Key format: `recent_screens_user_{userId}`
 *
 * ⚠️  Does NOT inject AuthService — avoids circular DI.
 *     Token is read directly from sessionStorage (same as AuthService.getAccessToken()).
 */
@Injectable({ providedIn: 'root' })
export class RecentScreensService {

  private readonly KEY_PREFIX = 'recent_screens_user_';

  private _screens$ = new BehaviorSubject<RecentScreen[]>(DEFAULT_RECENT_SCREENS);

  /** Read-only stream — components must subscribe to this, never access the Subject. */
  readonly recentScreens$: Observable<RecentScreen[]> = this._screens$.asObservable();

  private currentUserId: string | null = null;

  constructor(private jwtService: JwtService) {}

  // ─── Public API ────────────────────────────────────────────────────────────

  /**
   * Load recent screens for the given userId from storage.
   * Falls back to DEFAULT_RECENT_SCREENS when no history exists yet.
   * Call on: app init, after login, after user switch.
   */
  loadForUser(userId: string): void {
    this.currentUserId = userId;
    const stored = this.readFromStorage(userId);
    // Use defaults when the user has no history yet
    this._screens$.next(stored.length > 0 ? stored : DEFAULT_RECENT_SCREENS);
  }

  /**
   * Record that a screen was visited or searched.
   * Applies deduplication and max-7 rule via helper.
   * Emits the updated list and persists it.
   */
  trackVisit(screen: RecentScreen): void {
    if (!this.currentUserId) {
      this.tryResolveUserId();
      if (!this.currentUserId) return;
    }

    const current = this._screens$.getValue();
    // ✅ Always merge with the full current list (default or dynamic).
    // addToRecentScreens handles dedup and trims the last item via slice(0, MAX_RECENT_SCREENS).
    // We do NOT discard defaults when the first dynamic screen is added.
    const updated = addToRecentScreens(current, screen);
    this._screens$.next(updated);
    this.writeToStorage(this.currentUserId, updated);
  }

  /**
   * Clear recent screens for a user (explicit clear action).
   * Resets to defaults rather than empty — better UX.
   */
  clearForUser(userId: string): void {
    this.writeToStorage(userId, []);
    if (this.currentUserId === userId) {
      this._screens$.next(DEFAULT_RECENT_SCREENS);
    }
  }

  /** Returns the current snapshot synchronously. */
  getSnapshot(): RecentScreen[] {
    return this._screens$.getValue();
  }

  // ─── Internal ──────────────────────────────────────────────────────────────

  private storageKey(userId: string): string {
    return `${this.KEY_PREFIX}${userId}`;
  }

  /** Read + parse + sanitize from localStorage. Returns [] on any error. */
  private readFromStorage(userId: string): RecentScreen[] {
    try {
      const raw = localStorage.getItem(this.storageKey(userId));
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return sanitizeStoredScreens(parsed);
    } catch {
      console.warn('[RecentScreens] Failed to parse stored screens — resetting.');
      return [];
    }
  }

  /** Serialize and write to localStorage. Silently handles quota errors. */
  private writeToStorage(userId: string, screens: RecentScreen[]): void {
    try {
      localStorage.setItem(this.storageKey(userId), JSON.stringify(screens));
    } catch {
      console.warn('[RecentScreens] Could not persist to localStorage. Continuing in-memory.');
    }
  }

  /**
   * Lazily resolve userId from sessionStorage token directly.
   * ⚠️  Reads sessionStorage directly — intentionally avoids injecting AuthService
   *     to prevent circular DI (AuthService ↔ RecentScreensService).
   */
  private tryResolveUserId(): void {
    try {
      const token = sessionStorage.getItem('accessToken');
      if (token) {
        const userId = this.jwtService.getUserId(token);
        if (userId) {
          this.currentUserId = userId;
          this.loadForUser(userId);
        }
      }
    } catch {
      // No valid token — silent fail
    }
  }
}
